import { TranslateService } from '@ngx-translate/core';
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { cloneDeep, orderBy } from 'lodash';
import { NoteService } from './note.service';
import { GeneralUtilsService } from './common/generalUtils.service';
import { Crop, Farm, Field } from '../_models';
import { UILegend } from '../_models/common/UILegend';
import { CommodityService } from './commodity.service';
import { PolygonBaseColor } from '../../../assets/data/enum/colorConfiguration';
import { StableService } from './stable.service';
import { Stable } from '../_models/stable';

@Injectable()
export class MapLegendaService {
  colorListMap$: BehaviorSubject<Map<number, string>> = new BehaviorSubject(new Map());
  legendDataList$: BehaviorSubject<UILegend[]> = new BehaviorSubject([]);

  constructor(
    private generalUtilsService: GeneralUtilsService,
    private commodityService: CommodityService,
    private translate: TranslateService,
    private noteService: NoteService,
    private stableService: StableService
  ) {}

  buildLegendaAndColorByCompany(farmList: Farm[], fieldList: Field[]) {
    const colorMap: Map<number, string> = new Map();
    const legendList: UILegend[] = [];
    const randomColors: Map<number, string> = new Map();
    farmList.forEach((farm) => {
      if (!randomColors.get(farm.id)) {
        randomColors.set(farm.id, this.generalUtilsService.getRandomColor());
        const legendItem: UILegend = new UILegend();
        legendItem.id = farm.id;
        legendItem.color = randomColors.get(farm.id);
        legendItem.value = farm.name;
        legendList.push(legendItem);
      }
      fieldList.forEach((field) => {
        colorMap.set(field.id, randomColors.get(field.companyId));
      });
    });
    this.colorListMap$.next(colorMap);
    this.legendDataList$.next(legendList);
  }

  buildLegendaAndColorByCommodities(cropList: Crop[], fieldList: Field[], stableList?: Stable[]) {
    const colorMap: Map<number, string> = new Map();
    const legendList: UILegend[] = [];
    const cropColors: Map<number, string> = new Map();
    cropList.forEach((crop: Crop) => {
      if (!cropColors.get(crop.commodity.id)) {
        cropColors.set(
          crop.commodity.id,
          this.commodityService.getCommodityColorById(crop.commodity.id)
        );
        const legendItem: UILegend = new UILegend();
        legendItem.id = crop.commodity.id;
        legendItem.color = cropColors.get(crop.commodity.id);
        legendItem.value = this.translate.instant('select_commodity.' + crop.commodity.name);
        legendList.push(legendItem);
      }
    });
    fieldList.forEach((field) => {
      if (field.crops.length > 0 && cropList && cropList.length > 0) {
        const tmpExistCropFiltered = field.crops.filter(
          (item: Crop) => item.commodity !== undefined && item.year == cropList[0].year
        );
        const existCropFiltered = orderBy(tmpExistCropFiltered, 'dateCreated', 'asc');
        const cloneExistCrop = cloneDeep(existCropFiltered);
        const cropFilteredForPrimaryCrop = cloneExistCrop.filter(
          (item: Crop) => item.isPrimary == true
        );
        if (
          cropFilteredForPrimaryCrop[0] &&
          cropFilteredForPrimaryCrop[0].commodity &&
          cropFilteredForPrimaryCrop[0].commodity.id
        ) {
          colorMap.set(field.id, cropColors.get(cropFilteredForPrimaryCrop[0].commodity.id));
        } else if (
          existCropFiltered[0] &&
          existCropFiltered[0].commodity &&
          existCropFiltered[0].commodity.id
        ) {
          colorMap.set(field.id, cropColors.get(existCropFiltered[0].commodity.id));
        }
      }
    });
    if (stableList && stableList.length) {
      const stableLegendItem = new UILegend();
      stableLegendItem.id = -1;
      stableLegendItem.color = PolygonBaseColor.STABLE;
      stableLegendItem.value = this.translate.instant('livestock.stables');
      legendList.push(stableLegendItem);
    }
    this.colorListMap$.next(colorMap);
    this.legendDataList$.next(legendList);
  }

  buildLegendaAndColorByFieldGroups(filteredFieldList: Field[], fieldList: Field[]) {
    const colorMap: Map<number, string> = new Map();
    const legendList: UILegend[] = [];
    const randomColors: Map<number, string> = new Map();
    filteredFieldList.forEach((field) => {
      if (field.group && field.group.id && !randomColors.get(field.group.id)) {
        randomColors.set(field.group.id, this.generalUtilsService.getRandomColor());
        const legendItem: UILegend = new UILegend();
        legendItem.id = field.group.id;
        legendItem.color = randomColors.get(field.group.id);
        legendItem.value = field.group.name;
        legendList.push(legendItem);
      }
      fieldList.forEach((fieldItem) => {
        if (fieldItem.group && fieldItem.group.id) {
          colorMap.set(fieldItem.id, randomColors.get(fieldItem.group.id));
        } else {
          colorMap.set(fieldItem.id, '#000000');
        }
      });
    });
    this.colorListMap$.next(colorMap);
    this.legendDataList$.next(legendList);
  }

  buildLegendaByNotes() {
    const legendList: UILegend[] = [];
    this.noteService.getAllNoteTypes().forEach((noteType: string, index: number) => {
      const legendItem: UILegend = new UILegend();
      legendItem.id = index;
      legendItem.color = this.noteService.getNoteTypeColor(noteType);
      legendItem.value = this.translate.instant('noteType.' + noteType);
      legendList.push(legendItem);
    });
    this.colorListMap$.next(new Map());
    this.legendDataList$.next(legendList);
  }

  getLegendaByNotes(): UILegend[] {
    const legendList: UILegend[] = [];
    this.noteService.getAllNoteTypes().forEach((noteType: string, index: number) => {
      const legendItem: UILegend = new UILegend();
      legendItem.id = index;
      legendItem.color = this.noteService.getNoteTypeColor(noteType);
      legendItem.value = this.translate.instant('noteType.' + noteType);
      legendList.push(legendItem);
    });
    return legendList;
  }
  buildLegendaAndColorByProductionOrientation(stableList: Stable[], fieldList?: Field[]) {
    const colorMap: Map<number, string> = new Map();
    const legendList: UILegend[] = [];

    const stablesColorMap = this.stableService.getProductionOrientationColorMap();
    stableList.forEach((s: Stable) => {
      if (s && s.id) {
        const type = s.productionOrientation;
        const label = this.translate.instant('animals.productive_orientations_key.' + type);
        let color = stablesColorMap.get(type);
        if (!color) {
          color = this.generalUtilsService.getRandomColor().valueOf();
          stablesColorMap.set(type, color);
        }
        colorMap.set(s.id, color);
        const legendIndex = legendList.findIndex((l: any) => l.value === label);
        if (legendIndex === -1) {
          const legendItem: UILegend = new UILegend();
          legendItem.id = legendList.length + 1;
          legendItem.color = color;
          legendItem.value = label;
          legendList.push(legendItem);
        }
      }
    });

    if (fieldList && fieldList.length) {
      const fieldLegendItem = new UILegend();
      fieldLegendItem.id = -1;
      fieldLegendItem.color = PolygonBaseColor.FIELD;
      fieldLegendItem.value = this.translate.instant('livestock.cultivated_fields');
      legendList.push(fieldLegendItem);
    }

    this.colorListMap$.next(colorMap);
    this.legendDataList$.next(legendList);
  }
}

export class LegendaSelection {
  static GROUPS = 'groups';
  static COMMODITIES = 'commodities';
  static COMPANIES = 'companies';
  static NOTES = 'notes';
  static PRODUCTION_ORIENTATION = 'production_orientation';
}
